import { useMemo } from "react";

import { useSales } from "@/Apollo/hooks/order/useSales";
import {
  Language,
  Ordering,
  PayoutHoldReason,
  SalesSort,
} from "@/Apollo/schema";
import { HeldOrderLine } from "@/components/Account/payment/payment.utils";
import {
  SALES_FETCH_LIMIT,
  SELLER_SALE_STATUS,
} from "@/components/Account/sales/sales.static";
import { getSellerSaleStatus } from "@/components/Account/sales/sales.utils";

/**
 * The seller's recent sales whose payout waits for something, for the
 * "What is waiting" breakdown. Only fetched where the block renders.
 */
export const useHeldOrders = (enabled: boolean, language: Language) => {
  const { data } = useSales({
    variables: {
      productTitleQuery: "",
      page: 1,
      limit: SALES_FETCH_LIMIT,
      sort: SalesSort.Created,
      order: Ordering.Desc,
      lang: language,
    },
    skip: !enabled,
  });

  return useMemo<HeldOrderLine[]>(() => {
    const lines: HeldOrderLine[] = [];

    for (const order of data?.sales.items ?? []) {
      const holdReason = order.payoutDetails?.hold_reason;

      if (!holdReason || holdReason === PayoutHoldReason.None) {
        continue;
      }

      // Only money that is still waiting: a dispute on a sale that was
      // paid out or cancelled since holds nothing.
      const saleStatus = getSellerSaleStatus(
        order.payoutDetails?.status,
        true,
        order.payout_status,
        { holdReason, paidConfirmed: order.payoutDetails?.paid_confirmed }
      );

      if (
        saleStatus !== SELLER_SALE_STATUS.pending &&
        saleStatus !== SELLER_SALE_STATUS.actionNeeded
      ) {
        continue;
      }

      lines.push({
        id: order.id,
        title: order.product?.title ?? "",
        payout: order.payout ?? 0,
        created: order.created ?? null,
        holdReason,
        threadId: order.thread?.id ?? null,
      });
    }

    return lines;
  }, [data]);
};
